import React, { useState, useEffect } from 'react';
import { notificationAPI } from '../../api/notifications';
import toast from 'react-hot-toast';
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, deleteToken, isSupported } from 'firebase/messaging';

const Preferences = () => {
  const [preferences, setPreferences] = useState({
    email_enabled: true,
    sms_enabled: true,
    push_enabled: true,
    booking_confirmations: true,
    booking_reminders: true,
    promotions: false,
    subscription_alerts: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushInfo, setPushInfo] = useState({
    permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
    token: localStorage.getItem('webPushToken') || null
  });

  useEffect(() => {
    loadPreferences();
  }, []);

  const firebaseConfig = {
    apiKey: "AIzaSyDhFhTkSHbadYW-d1dH2A8e52V4-40qbE0",
    authDomain: "saas-823ac.firebaseapp.com",
    projectId: "saas-823ac",
    storageBucket: "saas-823ac.firebasestorage.app",
    messagingSenderId: "806168481737",
    appId: "1:806168481737:web:0f7ba368d8a42b59d76218",
    measurementId: "G-NDKJ2KCHYJ"
  };

  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || 'BCYKudJpj_L7o16jST3gTbscLcb9aVzZwrj7_hPOffcIandbBjPbHhW7i41FzAKk1zaofo_9bq6Qqhw3yl2qsh8';

  const ensureFirebaseApp = () => {
    if (getApps().length > 0) return getApps()[0];
    return initializeApp(firebaseConfig);
  };

  const registerWebPush = async () => {
    setPushLoading(true);
    try {
      if (!('serviceWorker' in navigator)) {
        toast.error('Service Worker not supported in this browser');
        return;
      }
      if (!('Notification' in window)) {
        toast.error('Notifications not supported in this browser');
        return;
      }

      const supported = await isSupported();
      if (!supported) {
        toast.error('Push messaging not supported in this browser');
        return;
      }

      const permission = await Notification.requestPermission();
      setPushInfo((prev) => ({ ...prev, permission }));
      if (permission !== 'granted') {
        toast.error('Notification permission not granted');
        return;
      }

      const app = ensureFirebaseApp();
      const messaging = getMessaging(app);
      const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swRegistration });

      if (!token) {
        toast.error('Failed to get push token');
        return;
      }

      await notificationAPI.registerDeviceToken({ token, platform: 'web' });
      localStorage.setItem('webPushToken', token);
      setPushInfo({ permission: 'granted', token });
      toast.success('Push notifications enabled');
    } catch (e) {
      toast.error('Failed to enable push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  const unregisterWebPush = async () => {
    setPushLoading(true);
    try {
      const existingToken = localStorage.getItem('webPushToken');
      if (!existingToken) {
        toast.error('No push token found');
        return;
      }

      await notificationAPI.unregisterDeviceToken({ token: existingToken, platform: 'web' });

      try {
        const app = ensureFirebaseApp();
        const messaging = getMessaging(app);
        await deleteToken(messaging);
      } catch (e) {
      }

      localStorage.removeItem('webPushToken');
      setPushInfo((prev) => ({ ...prev, token: null }));
      toast.success('Push notifications disabled');
    } catch (e) {
      toast.error('Failed to disable push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  const loadPreferences = async () => {
    try {
      const response = await notificationAPI.getPreferences();
      setPreferences(response.data.data);
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key) => {
    const newPreferences = { ...preferences, [key]: !preferences[key] };
    setPreferences(newPreferences);
    setSaving(true);
    try {
      await notificationAPI.updatePreferences(newPreferences);
    } catch (error) {
      console.error('Failed to save preferences:', error);
      // Revert on error
      setPreferences(preferences);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading preferences...</div>;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Notification Preferences</h1>
        <p className="text-gray-600">Manage how you receive notifications</p>
      </div>

      <div className="bg-white rounded-lg shadow-md divide-y">
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Channels</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-gray-500">Receive notifications via email</p>
              </div>
              <button
                onClick={() => handleToggle('email_enabled')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  preferences.email_enabled ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences.email_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">SMS Notifications</p>
                <p className="text-sm text-gray-500">Receive notifications via text message</p>
              </div>
              <button
                onClick={() => handleToggle('sms_enabled')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  preferences.sms_enabled ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences.sms_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">Push Notifications</p>
                <p className="text-sm text-gray-500">Receive notifications on this device</p>
              </div>
              <button
                onClick={() => handleToggle('push_enabled')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  preferences.push_enabled ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences.push_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          <h2 className="text-lg font-semibold mb-2">Web Push Setup</h2>
          <div className="text-sm text-gray-600 space-y-2">
            <div className="flex justify-between">
              <span>Permission</span>
              <span className="font-medium">{pushInfo.permission}</span>
            </div>
            <div className="flex justify-between">
              <span>Token</span>
              <span className="font-medium">
                {pushInfo.token ? `${pushInfo.token.slice(0, 10)}...${pushInfo.token.slice(-8)}` : 'Not registered'}
              </span>
            </div>
          </div>

          <div className="mt-4 flex gap-3">
            <button
              onClick={registerWebPush}
              disabled={pushLoading || !preferences.push_enabled}
              className={`px-4 py-2 rounded-md text-white ${
                pushLoading || !preferences.push_enabled ? 'bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'
              }`}
            >
              Enable on this device
            </button>

            <button
              onClick={unregisterWebPush}
              disabled={pushLoading || !pushInfo.token}
              className={`px-4 py-2 rounded-md text-white ${
                pushLoading || !pushInfo.token ? 'bg-gray-400' : 'bg-gray-700 hover:bg-gray-800'
              }`}
            >
              Disable on this device
            </button>
          </div>
        </div>

        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Notification Types</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">Booking Confirmations</p>
                <p className="text-sm text-gray-500">When you make or receive a booking</p>
              </div>
              <button
                onClick={() => handleToggle('booking_confirmations')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  preferences.booking_confirmations ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences.booking_confirmations ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">Booking Reminders</p>
                <p className="text-sm text-gray-500">Reminders before your appointments</p>
              </div>
              <button
                onClick={() => handleToggle('booking_reminders')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  preferences.booking_reminders ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences.booking_reminders ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">Promotions & Offers</p>
                <p className="text-sm text-gray-500">Special deals and promotions</p>
              </div>
              <button
                onClick={() => handleToggle('promotions')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  preferences.promotions ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences.promotions ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">Subscription Alerts</p>
                <p className="text-sm text-gray-500">Payment and subscription reminders</p>
              </div>
              <button
                onClick={() => handleToggle('subscription_alerts')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  preferences.subscription_alerts ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences.subscription_alerts ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {saving && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg">
          Saving changes...
        </div>
      )}
    </div>
  );
};

export default Preferences;
